import { LockIcon, StarIcon } from "lucide-react"

import { Badge, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"

import { ReportBadge, resolveReportBadge, type FinanceReportEntry } from "@/features/reports/model/report-registry-types"

export const REPORT_BADGE_LABEL: Record<ReportBadge, string> = {
  [ReportBadge.New]: "finance.reports.badges.new",
  [ReportBadge.Beta]: "finance.reports.badges.beta",
  [ReportBadge.ComingSoon]: "finance.reports.badges.comingSoon",
  [ReportBadge.Parish]: "finance.reports.badges.parish",
}

/**
 * A badge only helps if its colour says something — and only works at all if it is SEEN.
 *
 * `New` is green because it is good news and an invitation to look — the same green the app uses for a
 * healthy state. `Beta` and `Coming soon` stay quiet: they are caveats, not news, and dressing a caveat in
 * the New colour would train people to ignore the one badge that is worth noticing.
 *
 * `Parish` is `info` (sky) rather than the zinc `secondary`, which on a dark card beside a grey title reads
 * as part of the title. It is not decoration: it is the only thing distinguishing a report covering the
 * parish's own books from the church-wide ones around it. A status colour would be wrong — a scope label
 * must not be misread as a verdict on the numbers — and sky sits apart from the emerald used elsewhere here.
 */
export const REPORT_BADGE_VARIANT: Record<ReportBadge, "success" | "secondary" | "outline" | "info"> = {
  [ReportBadge.New]: "success",
  [ReportBadge.Beta]: "secondary",
  [ReportBadge.ComingSoon]: "outline",
  [ReportBadge.Parish]: "info",
}

export interface ReportCardProps {
  entry: FinanceReportEntry
  title: string
  description?: string
  isFavorite: boolean
  /**
   * Why this report cannot be run, when it cannot (ADR-0024).
   *
   * A branch-dimension report under branch autonomy would state the figures of branches the reader may not
   * see, so the server refuses it. Without saying so here the card looked runnable and the only way to find
   * out was to click and hit a wall — the same "refusal shown as a failure" mistake, one screen earlier.
   */
  lockedReasonKey?: string
  onOpen: () => void
  onToggleFavorite: () => void
}

/** A compact catalog card: icon, title, description, optional badge, favorite star. */
export function ReportCard({
  entry,
  title,
  description,
  isFavorite,
  lockedReasonKey,
  onOpen,
  onToggleFavorite,
}: ReportCardProps) {
  // What it is wearing TODAY — a `newUntil` date expires the New badge without anyone tidying up.
  const badge = resolveReportBadge(entry)
  const t = useTranslations()
  // A locked report is not "disabled pending something" — it is settled. Both stop the click; only one
  // explains itself, so the reason replaces the description rather than sitting beside it.
  const isLocked = Boolean(lockedReasonKey)
  const isBlocked = entry.disabled || isLocked
  const Icon = isLocked ? LockIcon : entry.icon
  const isMobile = useIsMobile()

  if (isMobile) {
    // Phone: one row of the section's grouped card (the section draws the surface and the hairlines). The star
    // is always shown, since a phone has no hover to reveal it.
    return (
      <div
        data-testid="report-card"
        data-report-id={entry.id}
        className={cn(
          "flex min-h-[3.75rem] w-full min-w-0 items-center gap-3 py-2.5 pr-2 pl-4",
          isBlocked ? "opacity-60" : "cursor-pointer active:bg-muted",
        )}
        onClick={isBlocked ? undefined : onOpen}
        role={isBlocked ? undefined : "button"}
        tabIndex={isBlocked ? undefined : 0}
        aria-disabled={isBlocked || undefined}
        onKeyDown={(event) => {
          if (!isBlocked && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault()
            onOpen()
          }
        }}
      >
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary [&_svg]:size-4">
          <Icon aria-hidden="true" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[15px] leading-tight font-extrabold text-foreground">{title}</span>
            {badge ? (
              <Badge variant={REPORT_BADGE_VARIANT[badge]} className="shrink-0 px-1.5 py-0 text-[10px]">
                {t(REPORT_BADGE_LABEL[badge] as never)}
              </Badge>
            ) : null}
          </span>
          {isLocked ? (
            <span className="truncate text-xs font-semibold text-muted-foreground">{t(lockedReasonKey as never)}</span>
          ) : description ? (
            <span className="truncate text-xs font-semibold text-muted-foreground">{description}</span>
          ) : null}
        </span>
        <button
          type="button"
          aria-label={t("finance.reports.favorite" as never)}
          aria-pressed={isFavorite}
          onClick={(event) => {
            event.stopPropagation()
            onToggleFavorite()
          }}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground/50 active:bg-muted"
        >
          <StarIcon className={cn("size-5", isFavorite && "fill-amber-400 text-amber-500")} />
        </button>
      </div>
    )
  }

  return (
    <div
      data-testid="report-card"
      data-report-id={entry.id}
      className={cn(
        "group/report relative flex items-center gap-2.5 rounded-lg border bg-card p-2.5 transition-colors",
        isBlocked ? "opacity-60" : "cursor-pointer hover:border-primary/40 hover:bg-primary/[0.08]",
      )}
      onClick={isBlocked ? undefined : onOpen}
      role={isBlocked ? undefined : "button"}
      tabIndex={isBlocked ? undefined : 0}
      aria-disabled={isBlocked || undefined}
      title={isLocked ? t(lockedReasonKey as never) : undefined}
      onKeyDown={(event) => {
        if (!isBlocked && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault()
          onOpen()
        }
      }}
    >
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary [&_svg]:size-3.5">
        <Icon aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {/* No link underline: the whole card is the click target, so the title is not a separate link. */}
          <h3 className="truncate text-sm font-medium text-foreground">{title}</h3>
          {badge ? (
            <Badge variant={REPORT_BADGE_VARIANT[badge]} className="shrink-0 px-1.5 py-0 text-[10px]">
              {t(REPORT_BADGE_LABEL[badge] as never)}
            </Badge>
          ) : null}
        </div>
        {/* The REASON, not the pitch: a locked card should say why rather than advertise what it would show. */}
        {isLocked ? (
          <p className="truncate text-xs text-muted-foreground">{t(lockedReasonKey as never)}</p>
        ) : description ? (
          <p className="truncate text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>

      <button
        type="button"
        aria-label={t("finance.reports.favorite" as never)}
        onClick={(event) => {
          event.stopPropagation()
          onToggleFavorite()
        }}
        className={cn(
          "shrink-0 text-muted-foreground/50 transition-colors hover:text-amber-500",
          !isFavorite && "opacity-0 group-hover/report:opacity-100 focus-visible:opacity-100",
        )}
      >
        <StarIcon className={cn("size-4", isFavorite && "fill-amber-400 text-amber-500")} />
      </button>
    </div>
  )
}
